// MySQL-based Notification Service for Admin Panel
// Uses MySQL database instead of Firebase for notification management

class MySQLNotificationService {
  constructor() {
    // Use your live server URL
    this.baseURL = 'https://mandilive.in/api'; // Your live backend server
    this.isReady = false;
    
    console.log('🗄️ MySQL Notification Service initialized (Live Server)');
    console.log('🌐 Using server:', this.baseURL);
    this.testConnection();
  }

  /**
   * Test API connection
   */
  async testConnection() {
    try {
      const response = await fetch(`${this.baseURL}/test.php`);
      this.isReady = response.ok;
      console.log('✅ MySQL API connection test:', this.isReady ? 'Success' : 'Failed');
    } catch (error) {
      console.warn('⚠️ MySQL API connection failed:', error.message);
      this.isReady = false;
    }
  }

  /**
   * Create and send notification to all app users
   * @param {Object} notificationData - Notification details
   * @returns {Promise<Object>} - Result with notification ID
   */
  async createAndSendNotification(notificationData) {
    try {
      console.log('📤 Creating MySQL notification:', notificationData.title);
      
      // Validate required fields
      if (!notificationData.title || !notificationData.message) {
        throw new Error('Title and message are required');
      }

      // Step 1: Create notification in MySQL database
      const createResponse = await fetch(`${this.baseURL}/endpoints/notifications.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: notificationData.title,
          message: notificationData.message,
          type: notificationData.type || 'general',
          priority: notificationData.priority || 'normal',
          createdBy: notificationData.createdBy || 'Admin',
          imageUrl: notificationData.imageUrl || null,
          actionUrl: notificationData.actionUrl || null
        })
      });

      if (!createResponse.ok) {
        throw new Error(`Failed to create notification: HTTP ${createResponse.status}`);
      }

      const createResult = await createResponse.json();
      if (!createResult.success) {
        throw new Error(createResult.message || 'Failed to create notification');
      }

      const notificationId = createResult.notificationId;
      console.log('✅ Notification created in MySQL:', notificationId);

      // Step 2: Send FCM notifications using the new API
      const sendResponse = await fetch(`${this.baseURL}/admin/notifications/send-mysql.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationId: notificationId,
          title: notificationData.title,
          message: notificationData.message,
          type: notificationData.type || 'general',
          priority: notificationData.priority || 'normal',
          imageUrl: notificationData.imageUrl || null,
          actionUrl: notificationData.actionUrl || null
        })
      });

      if (!sendResponse.ok) {
        throw new Error(`Failed to send notification: HTTP ${sendResponse.status}`);
      }

      const sendResult = await sendResponse.json();
      
      console.log('✅ Notification sending completed:', sendResult);

      return {
        success: sendResult.success,
        notificationId: notificationId,
        message: sendResult.success ? 'Notification sent successfully' : 'Failed to send notification',
        deliveryResult: sendResult,
        sentCount: sendResult.sentCount || 0,
        failedCount: sendResult.failedCount || 0
      };

    } catch (error) {
      console.error('❌ Error creating MySQL notification:', error);
      throw error;
    }
  }

  /**
   * Get all notifications from MySQL database
   * @param {Function} callback - Callback for notifications data
   * @returns {Function} - Unsubscribe function
   */
  getNotifications(callback) {
    try {
      const fetchNotifications = async () => {
        try {
          const response = await fetch(`${this.baseURL}/endpoints/notifications.php`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              callback(result.notifications || []);
            } else {
              console.error('Failed to fetch notifications:', result.message);
              callback([]);
            }
          } else {
            console.error('API response error:', response.status);
            callback([]);
          }
        } catch (error) {
          console.error('Error fetching notifications:', error);
          callback([]);
        }
      };

      // Initial load
      fetchNotifications();

      // Set up periodic refresh every 10 seconds
      const interval = setInterval(fetchNotifications, 10000);

      // Return unsubscribe function
      return () => {
        clearInterval(interval);
      };
    } catch (error) {
      console.error('❌ Error setting up notifications listener:', error);
      callback([]);
      return () => {};
    }
  }

  /**
   * Get notification analytics from MySQL
   * @returns {Promise<Object>} - Analytics data
   */
  async getAnalytics() {
    try {
      const response = await fetch(`${this.baseURL}/admin/notifications/analytics.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.success ? result.analytics : this.getDefaultAnalytics();
      } else {
        console.error('Analytics API error:', response.status);
        return this.getDefaultAnalytics();
      }
    } catch (error) {
      console.error('❌ Error getting analytics:', error);
      return this.getDefaultAnalytics();
    }
  }

  /**
   * Default analytics structure
   */
  getDefaultAnalytics() {
    return {
      totalNotifications: 0,
      totalSent: 0,
      totalDelivered: 0,
      totalOpened: 0,
      totalFailed: 0,
      deliveryRate: 0,
      openRate: 0,
      typeStats: {},
      recentNotifications: []
    };
  }

  /**
   * Delete notification from MySQL
   * @param {string} notificationId - Notification ID to delete
   * @returns {Promise<Object>} - Delete result
   */
  async deleteNotification(notificationId) {
    try {
      console.log('🗑️ Attempting to delete notification with ID:', notificationId);
      
      // Send ID in request body since URL path parsing might not work on live server
      const endpoint = `${this.baseURL}/endpoints/notifications.php`;
      
      console.log('🔄 Using endpoint:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: notificationId,
          notification_id: notificationId
        })
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Delete response:', result);
        
        return {
          success: result.success,
          message: result.message || 'Notification deleted successfully'
        };
      } else {
        // Log response text for debugging
        const errorText = await response.text();
        console.error('❌ Delete failed with status:', response.status, 'Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Get user statistics from database
   * @returns {Promise<Object>} - User statistics
   */
  async getUserStats() {
    try {
      const response = await fetch(`${this.baseURL}/admin/notifications/user-stats.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.success ? result.stats : {
          totalUsers: 0,
          activeUsers: 0,
          usersWithNotifications: 0
        };
      } else {
        return {
          totalUsers: 0,
          activeUsers: 0,
          usersWithNotifications: 0
        };
      }
    } catch (error) {
      console.error('❌ Error getting user stats:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        usersWithNotifications: 0
      };
    }
  }

  /**
   * Get notification types for dropdown
   * @returns {Array} - Array of notification types
   */
  getNotificationTypes() {
    return [
      { value: 'price_update', label: 'Price Update', icon: '💰' },
      { value: 'weather_alert', label: 'Weather Alert', icon: '🌦️' },
      { value: 'scheme_update', label: 'Government Scheme', icon: '🏛️' },
      { value: 'app_update', label: 'App Update', icon: '📱' },
      { value: 'mandi_news', label: 'Mandi News', icon: '📰' },
      { value: 'general', label: 'General', icon: '📢' }
    ];
  }

  /**
   * Get priority levels
   * @returns {Array} - Array of priority levels
   */
  getPriorityLevels() {
    return [
      { value: 'high', label: 'High Priority', color: 'red' },
      { value: 'normal', label: 'Normal', color: 'blue' },
      { value: 'low', label: 'Low Priority', color: 'gray' }
    ];
  }

  /**
   * Test sending notification to a specific FCM token
   * @param {string} token - FCM token to test
   * @param {Object} testMessage - Test message data
   * @returns {Promise<Object>} - Test result
   */
  async testFCMToken(token, testMessage = null) {
    try {
      const defaultMessage = {
        title: 'Test Notification',
        message: 'This is a test notification from your admin panel',
        type: 'general',
        priority: 'normal'
      };

      const message = testMessage || defaultMessage;

      const response = await fetch(`${this.baseURL}/admin/notifications/test-token.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          ...message
        })
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: result.success,
          message: result.message,
          details: result.details || null
        };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error testing FCM token:', error);
      return {
        success: false,
        message: 'Test failed: ' + error.message
      };
    }
  }
}

// Export singleton instance
const mysqlNotificationService = new MySQLNotificationService();
export default mysqlNotificationService;
